import React, { useEffect } from 'react';
import { styled } from '@linaria/react';
import { m, LayoutGroup, AnimatePresence, useWillChange } from 'framer-motion';
import { COLORS } from '@livecommerce/design-system';
import BillingAlert from 'components-v3/BillingAlert/index.jsx';
import NotificationAlert from 'components-v3/NotificationAlert/index.jsx';
import _Sidebar from '../Sidebar/Sidebar.jsx';
import useBillingAlert from 'hooks/useBillingAlert.js';
import { useNavigationFrameContext } from 'contexts/NavigationFrameContext.jsx';
import { productsNavigationTransitionConfig, secondaryTransitionConfig } from 'constants/animationConfig.js';
import { useDrawerContext } from 'contexts/DrawerContext.jsx';
import Topbar from '../../Topbar.jsx';
import Footer from '../../Footer.jsx';
import TopbarGutter from '../../TopbarGutter.jsx';
import _ProductHeader from 'components-v3/ProductHeader/ProductHeader.jsx';
import Drawer from 'components-v3/Drawer/Drawer.jsx';

import {
  ALERT_HEIGHT,
  TOP_GAP,
  PRODUCT_HEADER_HEIGHT,
  CONTENT_HEIGHT,
  BOTTOM_GAP,
  LEFT_GAP,
  SIDEBAR_WIDTH,
  EXPANDED_SIDEBAR_WIDTH,
  CONTENT_WIDTH,
  RIGHT_GAP,
  AREA_OPACITY,
  SUB_PAGE_WIDTHS,
} from './constants.js';
import { SlotConsumer, useSlots } from 'contexts/SlotContext/index.jsx';
import { CONTENT_SUB_PAGE } from 'contexts/SlotContext/slots.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useUserNotificationsContext } from 'contexts/UserNotificationsContext.jsx';

const getContainerRows = ({ isAlertShown, isProductHeaderExpanded, isProductHeaderShown, isContentHeaderShown, isFooterShown }) => {
  const rows = [];
  if (isAlertShown) rows.push(`${ALERT_HEIGHT}px`);
  rows.push(`${TOP_GAP}px`);
  if (isProductHeaderShown) {
    rows.push(isProductHeaderExpanded ? 'auto' : `${PRODUCT_HEADER_HEIGHT}px`);
  }
  if (isContentHeaderShown) rows.push('auto');
  rows.push(CONTENT_HEIGHT);
  if (isFooterShown) rows.push('auto');
  rows.push(`${BOTTOM_GAP}px`);

  return rows.join(' ');
};

const getContainerColumns = ({ isSidebarShown }) => {
  const columns = [];
  columns.push(`${LEFT_GAP}px`);
  if (isSidebarShown) {
    columns.push(`${SIDEBAR_WIDTH}px`);
    columns.push(`${EXPANDED_SIDEBAR_WIDTH}px`);
  }
  columns.push(CONTENT_WIDTH);
  columns.push(`${RIGHT_GAP}px`);

  return columns.join(' ');
};

const getContainerAreas = ({ isAlertShown, isSidebarShown, isProductHeaderShown, isContentHeaderShown, isFooterShown }) => {
  const areas = [];
  if (isAlertShown) areas.push(`"alert ${isSidebarShown ? 'alert alert' : ''} alert alert"`);
  areas.push(`"topLeftGap ${isSidebarShown ? 'topGap topGap' : ''} topGap topRightGap"`);
  if (isProductHeaderShown) {
    areas.push(`"leftGap ${isSidebarShown ? 'productHeader productHeader' : ''} productHeader rightGap"`);
  }
  if (isContentHeaderShown) {
    areas.push(`"leftGap ${isSidebarShown ? 'sidebar contentHeader' : ''} contentHeader rightGap"`);
  }
  areas.push(`"leftGap ${isSidebarShown ? 'sidebar mainContent' : ''} mainContent rightGap"`);
  if (isFooterShown) {
    areas.push(`"leftGap ${isSidebarShown ? 'sidebar contentFooter' : ''} contentFooter rightGap"`);
  }
  areas.push(`"bottomLeftGap ${isSidebarShown ? 'bottomGap bottomGap' : ''} bottomGap bottomRightGap"`);
  return areas.join('\n');
};

const getPaddings = ({ contentHasPaddings }) => (contentHasPaddings ? '40px' : '0');

const AnimatedContainerDiv = ({ areas, rows, columns, ...props }) => <m.div {...props} />;

const Container = styled(AnimatedContainerDiv)`
  display: grid;
  grid-template-rows: ${(props) => props.rows};
  grid-template-columns: ${(props) => props.columns};
  grid-template-areas: ${(props) => props.areas};
  height: 100vh;
  background-color: ${COLORS.dark_700};
  overflow: hidden;
`;

const AnimatedContentDiv = React.forwardRef(({ drawerIsOpenOrAnimating, pointerEvents, ...props }, ref) => <m.div ref={ref} {...props} />);
const Content = styled(AnimatedContentDiv)`
  position: relative;
  overflow: hidden;
  background-color: ${COLORS.dark_700};
  padding: 0;
  grid-area: mainContent;
  transform-origin: bottom;
  pointer-events: ${(props) => props.pointerEvents};
  perspective: ${(props) => props.drawerIsOpenOrAnimating ? '1000px' : 'none'};
  z-index: 1;
`;

const ProductHeader = styled(m(_ProductHeader))`
  grid-area: productHeader;
  z-index: 3;
`;

const ContentHeaderContainer = styled(m.div)`
  grid-area: contentHeader;
  width: 100%;
  z-index: 2;

  & > div {
    margin-bottom: 8px;
  }
`;

const ContentHeader = styled(m.div)`
  display: flex;
  background-color: ${COLORS.white};
  border-radius: 16px;
  width: 100%;
  pointer-events: ${(props) => props.pointerEvents};

  & > div {
    width: 100%;
  }
`;

const GutterContainer = styled(m.div)`
  pointer-events: ${(props) => props.pointerEvents};
  border-radius: 16px;
`;

const ContentFooterContainer = styled(m.div)`
  grid-area: contentFooter;
  width: 100%;
  display: flex;
  margin-top: 16px;
`;

const getGridArea = ({ isContentHeaderShown, isAlertShown }) => {
  const rowStart = 3;
  const rowEnd = isContentHeaderShown ? 5 : 4;
  const adjustedRowStart = isAlertShown ? rowStart + 1 : rowStart;
  const adjustedRowEnd = isAlertShown ? rowEnd + 1 : rowEnd;

  return `${adjustedRowStart} / 2 / ${adjustedRowEnd} / 4`;
};

const Sidebar = styled(m(_Sidebar))`
  position: relative;
  display: grid;
  grid-area: ${(props) => (props.sidebarGridArea)};
  pointer-events: ${(props) => props.pointerEvents};
`;

const AlertContainer = styled(m.div)`
  display: flex;
  grid-area: alert;
`;

const PageWrapper = styled(m.div)`
  height: 100%;
  transform-style: flat;
  position: relative;
  min-height: 100%;
  display: flex;
  gap: 8px;
`;

const pageWidth = ({ subPageWidth }) => {
  switch (subPageWidth) {
    case SUB_PAGE_WIDTHS.EXTRA_LARGE:
      return 'max(80%, 1000px)';
    case SUB_PAGE_WIDTHS.LARGE:
      return '80%';
    case SUB_PAGE_WIDTHS.LARGE_FIXED:
      return '854px';
    case SUB_PAGE_WIDTHS.MEDIUM:
      return '50%';
    case SUB_PAGE_WIDTHS.MEDIUM_FIXED:
      return '600px';
    case SUB_PAGE_WIDTHS.SMALL:
      return '435px';
    default:
      return '0';
  }
};

const getPageWidth = (subPageWidth) => {
  switch (subPageWidth) {
    case SUB_PAGE_WIDTHS.EXTRA_LARGE:
      return '1000px';
    case SUB_PAGE_WIDTHS.LARGE:
      return '80%';
    case SUB_PAGE_WIDTHS.LARGE_FIXED:
      return 854;
    case SUB_PAGE_WIDTHS.MEDIUM:
      return '50%';
    case SUB_PAGE_WIDTHS.MEDIUM_FIXED:
      return 600;
    case SUB_PAGE_WIDTHS.SMALL:
      return 435;
    default:
      return 0;
  }
};

const ContentSubPage = styled(m.div)`
  height: 100%;
  display: flex;
  width: ${(props) => pageWidth(props)};
  transition: width 200ms;
`;

const StyledSlotConsumer = styled(SlotConsumer)`
  width: 100%;
`;

const AnimatedPage = ({ contentHasPaddings, ...props }) => <m.div {...props} />;

const Page = styled(AnimatedPage)`
  background-color: ${COLORS.white};
  position: relative;
  padding: ${(props) => getPaddings(props)};
  min-height: 100%;
  overflow: auto;
  height: 100%;
  flex: 1;
`;

const ProductsNavigation = ({ children }) => {
  const willChange = useWillChange();
  const { hasPublication } = useSlots();
  const hasContentSubPage = hasPublication(CONTENT_SUB_PAGE);
  const { drawerIsOpen } = useDrawerContext();
  const {
    isTopbarShown: isContentHeaderShown,
    isProductHeaderShown,
    isSidebarShown,
    contentHasPaddings,
    isProductHeaderExpanded,
    setIsProductHeaderExpanded,
    isSidebarExpanded,
    setSelectedNavigationItemTitle,
    subPageWidth,
    isFooterShown,
  } = useNavigationFrameContext();
  const { isAlertShown, numberOfOverdueInvoices, isDeactivated } = useBillingAlert();
  const { mostRecentBannerNotification, handleBannerNotificationDismiss } = useUserNotificationsContext();
  const [drawerIsOpenOrAnimating, setDrawerIsOpenOrAnimating] = React.useState(false);

  useEffect(() => {
    if (!drawerIsOpen) return;
    setDrawerIsOpenOrAnimating(true);
  }, [drawerIsOpen]);

  const contentAnimate = {
    x: isSidebarExpanded ? EXPANDED_SIDEBAR_WIDTH : 0,
    opacity: isSidebarExpanded || isProductHeaderExpanded ? AREA_OPACITY : 1,
  };

  useEffect(() => {
    const onKeyUp = event => {
      if (event.key !== 'Escape') return;

      setIsProductHeaderExpanded(false);
      trackingService.track({
        eventType: 'product-menu:close',
        trigger: 'keyboard',
      });
      if (isSidebarExpanded) {
        setSelectedNavigationItemTitle();
      }
    };

    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [setIsProductHeaderExpanded, isSidebarExpanded, setSelectedNavigationItemTitle]);

  const isBannerAlertShown = isAlertShown || mostRecentBannerNotification;
  let bannerAlertComponent = null;
  if (isAlertShown) {
    bannerAlertComponent = (
      <AlertContainer>
        <BillingAlert numberOfInvoices={numberOfOverdueInvoices} isDeactivated={isDeactivated} />
      </AlertContainer>
    );
  } else if (mostRecentBannerNotification) {
    bannerAlertComponent = (
      <AlertContainer>
        <NotificationAlert
          notification={mostRecentBannerNotification}
          handleBannerNotificationDismiss={handleBannerNotificationDismiss}
        />
      </AlertContainer>
    );
  }

  const handlePageWrapperAnimationComplete = React.useCallback(() => {
    if (!drawerIsOpen && drawerIsOpenOrAnimating) {
      setDrawerIsOpenOrAnimating(false);
    }
  }, [drawerIsOpen, drawerIsOpenOrAnimating]);

  return (
    <Container
      rows={getContainerRows({ isAlertShown: isBannerAlertShown, isProductHeaderExpanded, isProductHeaderShown, isContentHeaderShown, isFooterShown })}
      columns={getContainerColumns({ isSidebarShown })}
      areas={getContainerAreas({ isAlertShown: isBannerAlertShown, isSidebarShown, isProductHeaderShown, isContentHeaderShown, isFooterShown })}
      layout
    >
      <LayoutGroup>
        {bannerAlertComponent}
        {isSidebarShown && (
          <Sidebar
            layout
            initial={false}
            style={{ willChange }}
            animate={{ opacity: isProductHeaderExpanded ? AREA_OPACITY : 1 }}
            transition={productsNavigationTransitionConfig}
            pointerEvents={isProductHeaderExpanded ? 'none' : 'auto'}
            sidebarGridArea={getGridArea({ isContentHeaderShown, isAlertShown: isBannerAlertShown })}
          />
        )}
        {isProductHeaderShown && (
          <ProductHeader
            layout
            initial={false}
            style={{ willChange }}
            transition={productsNavigationTransitionConfig}
            setIsProductHeaderExpanded={setIsProductHeaderExpanded}
            isProductHeaderExpanded={isProductHeaderExpanded}
          />
        )}
        {isContentHeaderShown && (
          <ContentHeaderContainer>
            <ContentHeader
              layout
              initial={false}
              style={{ willChange }}
              animate={contentAnimate}
              transition={productsNavigationTransitionConfig}
              pointerEvents={isProductHeaderExpanded || isSidebarExpanded ? 'none' : 'auto'}
            >
              <Topbar id="navigation-topbar" />
            </ContentHeader>
            <GutterContainer
              layout
              initial={false}
              style={{ willChange }}
              animate={contentAnimate}
              transition={productsNavigationTransitionConfig}
              pointerEvents={isProductHeaderExpanded || isSidebarExpanded ? 'none' : 'auto'}
            >
              <TopbarGutter id="navigation-topbar-gutter" />
            </GutterContainer>
          </ContentHeaderContainer>
        )}
        <Content
          layout
          initial={false}
          style={{ willChange, borderRadius: '16px' }}
          animate={contentAnimate}
          transition={productsNavigationTransitionConfig}
          pointerEvents={isProductHeaderExpanded || isSidebarExpanded ? 'none' : 'auto'}
          drawerIsOpenOrAnimating={drawerIsOpenOrAnimating}
        >
          <PageWrapper
            initial={false}
            animate={{
              z: drawerIsOpen ? '-200px' : '0px',
              rotateY: drawerIsOpen ? '15deg' : '0deg',
              opacity: drawerIsOpen ? AREA_OPACITY : 1,
            }}
            onAnimationComplete={handlePageWrapperAnimationComplete}
            style={{ willChange }}
            transition={productsNavigationTransitionConfig}
          >
            <LayoutGroup>
              <Page
                layout={drawerIsOpen ? undefined : true}
                initial={false}
                contentHasPaddings={contentHasPaddings}
                transition={secondaryTransitionConfig}
                style={{ willChange, borderRadius: '16px' }}
              >
                {children}
              </Page>
              <AnimatePresence mode="popLayout">
                {hasContentSubPage && (
                  <ContentSubPage
                    layout
                    style={{ willChange }}
                    initial={{ x: getPageWidth(subPageWidth) }}
                    animate={{ x: 0 }}
                    exit={{ x: getPageWidth(subPageWidth) }}
                    transition={secondaryTransitionConfig}
                    subPageWidth={subPageWidth}
                  >
                    <StyledSlotConsumer name={CONTENT_SUB_PAGE} />
                  </ContentSubPage>
                )}
              </AnimatePresence>
            </LayoutGroup>
          </PageWrapper>
          <Drawer />
        </Content>
        {isFooterShown && (
          <ContentFooterContainer
            layout
            initial={{ height: 0, opacity: 0 }}
            style={{ willChange }}
            animate={{ ...contentAnimate , height: 'auto' }}
            transition={productsNavigationTransitionConfig}
            pointerEvents={isProductHeaderExpanded || isSidebarExpanded ? 'none' : 'auto'}
          >
            <Footer />
          </ContentFooterContainer>
        )}
      </LayoutGroup>
    </Container>
  );
};

export default ProductsNavigation;
